import React, { useEffect, useMemo } from 'react'

import FilterChip from 'components/Luxkit/Chips/FilterChip'
import { EmptyArray } from 'lib/array/arrayUtils'
import { FilterIcons, FilterLabels } from 'tripPlanner/models'
import { TripPlannerFilterTypeCount } from 'tripPlanner/utils/summary'
import LineSlidersVIcon from 'components/Luxkit/Icons/line/LineSlidersVIcon'
import useStableObjectReference from 'hooks/useStableObjectReference'

interface Props {
  itemCount: number
  availableSubFilters: Array<TripPlannerFilterTypeCount>
  selectedFilters: Array<App.TripPlannerSubFilterType>
  onClearFilters: () => void
  onToggleFilter: (filterType: App.TripPlannerSubFilterType) => void
}

function FilterChips({
  availableSubFilters = EmptyArray,
  selectedFilters,
  itemCount,
  onClearFilters,
  onToggleFilter,
}: Props) {
  // Avoid triggering the effect when the reference changes without the filters actually changing
  const availableSubFiltersStabilised = useStableObjectReference(availableSubFilters)

  useEffect(() => {
    onClearFilters()
  }, [availableSubFiltersStabilised, onClearFilters])

  const selectedFiltersSet = useMemo(
    () => new Set(selectedFilters),
    [selectedFilters],
  )

  return (
    <>
      <FilterChip
        key="allItems"
        data-testid="trip-filters-all"
        onClick={onClearFilters}
        size="medium"
        startIcon={<LineSlidersVIcon />}
        selected={selectedFiltersSet.size === 0}
      >
        All Items · {itemCount}
      </FilterChip>
      {availableSubFilters.map((filter: TripPlannerFilterTypeCount) => {
        const { filterType, count } = filter
        const Icon = FilterIcons[filterType]

        return (
          <FilterChip
            key={filterType}
            selected={selectedFiltersSet.has(filterType)}
            size="medium"
            startIcon={<Icon />}
            data-testid={'trip-filters-' + filterType}
            onClick={() => onToggleFilter(filterType)}
          >
            {FilterLabels[filterType]} · {count}
          </FilterChip>
        )
      })}
    </>
  )
}

export default FilterChips
